// src/components/modals/ViewSMSCampaignModal.jsx
import { X, MessageCircle, Users, Calendar, User, Eye, Clock, CheckCircle, AlertTriangle } from 'lucide-react'
import { Button } from '../ui/Button'

const ViewSMSCampaignModal = ({ isOpen, onClose, campaign }) => {
  if (!isOpen || !campaign) return null

  const handleClose = () => {
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {/* <div className="p-2 bg-blue-100 rounded-full">
              <Eye className="h-5 w-5 text-blue-600" />
            </div> */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900">SMS Campaign Details</h2>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Campaign Status Banner */}
          <div className={`p-4 rounded-lg border-l-4 ${
            campaign.status === 'completed' ? 'bg-green-50 border-green-400' :
            campaign.status === 'failed' ? 'bg-red-50 border-red-400' :
            campaign.status === 'running' ? 'bg-blue-50 border-blue-400' :
            'bg-gray-50 border-gray-400'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${campaign.statusConfig.color}`}>
                  <span className="mr-2">{campaign.statusConfig.icon}</span>
                  {campaign.statusConfig.label}
                </span>
                <div>
                  <h3 className="font-medium text-gray-900">{campaign.title}</h3>
                  <p className="text-sm text-gray-600">{campaign.statusConfig.description}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Campaign Information Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900 flex items-center">
                <MessageCircle className="h-5 w-5 mr-2 text-blue-600" />
                Campaign Information
              </h4>
              
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600">Campaign ID:</span>
                  <span className="text-sm text-gray-900 font-mono">{campaign.id}</span>
                </div>
                
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600">Title:</span>
                  <span className="text-sm text-gray-900 text-right flex-1 ml-4">{campaign.title}</span>
                </div>
                
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600">Status:</span>
                  <span className={`text-sm px-2 py-1 rounded ${campaign.statusConfig.color}`}>
                    {campaign.statusConfig.icon} {campaign.statusConfig.label}
                  </span>
                </div>
              </div>
            </div>

            {/* Customer Group Information */}
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900 flex items-center">
                <Users className="h-5 w-5 mr-2 text-green-600" />
                Target Audience
              </h4>
              
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="space-y-2">
                  <h5 className="font-medium text-green-900">{campaign.customerGroup.name}</h5>
                  <p className="text-sm text-green-700">{campaign.customerGroup.description}</p>
                  <div className="flex items-center text-sm text-green-600">
                    <Users className="h-4 w-4 mr-1" />
                    <span className="font-medium">{campaign.customerGroup.customerCount}</span>
                    <span className="ml-1">customers will receive this campaign</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="space-y-4">
            <h4 className="text-lg font-medium text-gray-900 flex items-center">
              <Clock className="h-5 w-5 mr-2 text-purple-600" />
              Timeline
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Created Information */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-gray-100 rounded-full">
                    <Calendar className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <h5 className="text-sm font-medium text-gray-900">Created</h5>
                    <p className="text-sm text-gray-600">{campaign.createdAtFormatted}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      by {campaign.createdBy.username} ({campaign.createdBy.role})
                    </p>
                  </div>
                </div>
              </div>

              {/* Updated Information */}
              {campaign.updatedBy && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-blue-100 rounded-full">
                      <User className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <h5 className="text-sm font-medium text-blue-900">Last Updated</h5>
                      <p className="text-sm text-blue-700">{campaign.updatedAtFormatted}</p>
                      <p className="text-xs text-blue-600 mt-1">
                        by {campaign.updatedBy.username} ({campaign.updatedBy.role})
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Campaign Actions Available */}
          {/* <div className="space-y-4">
            <h4 className="text-lg font-medium text-gray-900 flex items-center">
              <CheckCircle className="h-5 w-5 mr-2 text-indigo-600" />
              Available Actions
            </h4>
            
            <div className="p-4 bg-indigo-50 rounded-lg">
              <div className="space-y-2">
                <p className="text-sm text-indigo-700">
                  Based on the current status, the following actions are available:
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {campaign.status === 'draft' && (
                    <>
                      <span className="px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full">Edit</span>
                      <span className="px-3 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">Run</span>
                      <span className="px-3 py-1 bg-red-100 text-red-800 text-xs rounded-full">Delete</span>
                    </>
                  )}
                  {campaign.status === 'running' && (
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">View Only</span>
                  )}
                  {campaign.status === 'completed' && (
                    <>
                      <span className="px-3 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">Retry</span>
                      <span className="px-3 py-1 bg-red-100 text-red-800 text-xs rounded-full">Delete</span>
                    </>
                  )}
                  {campaign.status === 'failed' && (
                    <>
                      <span className="px-3 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">Retry</span>
                      <span className="px-3 py-1 bg-red-100 text-red-800 text-xs rounded-full">Delete</span>
                    </>
                  )}
                  <span className="px-3 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">View</span>
                </div>
              </div>
            </div>
          </div> */}

          {/* Status-specific Information */}
          {campaign.status === 'draft' && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <h5 className="text-sm font-medium text-yellow-800">Draft Campaign</h5>
                  <p className="text-sm text-yellow-700 mt-1">
                    This campaign is in draft status and has not been sent yet. You can still edit the campaign details or run it to start sending messages to customers.
                  </p>
                </div>
              </div>
            </div>
          )}

          {campaign.status === 'running' && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h5 className="text-sm font-medium text-blue-800">Campaign in Progress</h5>
                  <p className="text-sm text-blue-700 mt-1">
                    This campaign is currently running and messages are being sent to customers. Progress can be monitored through the SMS logs.
                  </p>
                </div>
              </div>
            </div>
          )}

          {campaign.status === 'completed' && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h5 className="text-sm font-medium text-green-800">Campaign Completed</h5>
                  <p className="text-sm text-green-700 mt-1">
                    This campaign has been successfully completed. All messages have been processed and sent to the target customers. You can view detailed logs to see the delivery status.
                  </p>
                </div>
              </div>
            </div>
          )}

          {campaign.status === 'failed' && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <h5 className="text-sm font-medium text-red-800">Campaign Failed</h5>
                  <p className="text-sm text-red-700 mt-1">
                    This campaign encountered errors during execution and was stopped. You can retry the campaign to attempt sending messages again, or check the SMS logs for detailed error information.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <Button
            onClick={handleClose}
            className="!bg-gray-100 hover:!bg-gray-200 !text-gray-700 border-gray-300 hover:border-gray-300"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ViewSMSCampaignModal